import Link from 'next/link'
import type { CommercialRouting } from '@/lib/blog-commercial-routing'

/**
 * Article Commercial Routing block (plan §0 item 36).
 *
 * Prominent, honest intent-split CTA rendered near the top of the article
 * (between the byline row and the takeaways) on the curated batch of
 * high-impression / no-click informational articles whose query mix is
 * commercial. Routes that intent to 1-2 verified live+indexed collections.
 *
 * Template injection only — article HTML in the DB is never modified (same
 * rule as ArticleToolCta). Targets arrive pre-verified from
 * getCommercialRouting (status='active' + seo_disposition='indexed').
 *
 * Visual language: light card (white background, owner rule), orange top
 * accent + blue CTA buttons — the same palette as .blog-key-specs /
 * .ed-takeaway.
 */
export function ArticleCommercialRouting({ routing }: { routing: CommercialRouting }) {
  return (
    <section
      aria-label="Shop this category"
      style={{
        background: '#fff',
        border: '1px solid var(--gray-200, #e5e7eb)',
        borderTop: '3px solid var(--fridge-orange, #ff9951)',
        borderRadius: 12,
        padding: '20px 24px',
        margin: '0 0 28px',
        boxShadow: '0 1px 3px rgba(0,0,0,.05)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'var(--fridge-orange, #ff9951)',
          marginBottom: 8,
        }}
      >
        Shop this category
      </div>
      <div
        style={{
          fontSize: 19,
          fontWeight: 800,
          letterSpacing: '-0.01em',
          lineHeight: 1.25,
          color: '#111827',
          marginBottom: 6,
        }}
      >
        {routing.headline}
      </div>
      <p
        style={{
          fontSize: 14.5,
          lineHeight: 1.55,
          color: '#4b5563',
          margin: '0 0 14px',
        }}
      >
        {routing.body}
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {routing.targets.map((t, i) => (
          <Link
            key={t.slug}
            href={t.url}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '10px 16px',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 700,
              textDecoration: 'none',
              ...(i === 0
                ? { background: 'var(--fridge-blue, #0469f0)', color: '#fff' }
                : {
                    background: '#fff',
                    color: 'var(--fridge-blue, #0469f0)',
                    border: '1px solid var(--fridge-blue, #0469f0)',
                  }),
            }}
          >
            Shop {t.title}
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        ))}
      </div>
    </section>
  )
}
